import type { Site } from "../types";
import type { ManagerReportResponse } from "../types/reports";
import { isSiteAccessActive } from "./site-status";

export type ManagerLaunchTaskId =
  | "create-site"
  | "activate-site"
  | "print-qr"
  | "first-sign-in";

export type ManagerLaunchTask = {
  id: ManagerLaunchTaskId;
  title: string;
  description: string;
  href: string;
  ctaLabel: string;
  complete: boolean;
  disabled?: boolean;
};

export type ManagerLaunchChecklistState = {
  shouldShow: boolean;
  progressLabel: string;
  primaryHref: string;
  primaryLabel: string;
  tasks: ManagerLaunchTask[];
};

type ManagerLaunchChecklistOptions = {
  sites?: Site[];
  report?: ManagerReportResponse | null;
};

export function createManagerLaunchChecklistState({
  sites,
  report,
}: ManagerLaunchChecklistOptions): ManagerLaunchChecklistState {
  const managedSites = sites ?? [];
  const firstSite = managedSites[0] ?? null;
  const firstActiveSite =
    managedSites.find((site) => isSiteAccessActive(site.status)) ?? null;
  const firstInactiveSite =
    managedSites.find((site) => !isSiteAccessActive(site.status)) ?? null;
  const totalSignIns = report?.summary.totalSignIns ?? 0;
  const hasSites = managedSites.length > 0;
  const hasActiveSite = Boolean(firstActiveSite);
  const hasSignIns = totalSignIns > 0;
  const qrSite = firstActiveSite ?? firstSite;
  const qrHref = qrSite ? `/sites/${qrSite.id}/qr` : "/sites/new";

  const tasks: ManagerLaunchTask[] = [
    {
      id: "create-site",
      title: "Create first site",
      description:
        "Add site location and geofence so workers have a place to sign in.",
      href: "/sites/new",
      ctaLabel: hasSites ? "Add another site" : "Add site",
      complete: hasSites,
    },
    {
      id: "activate-site",
      title: "Activate site access",
      description:
        "Active sites allow workers to sign in from QR codes and entrances.",
      href: firstInactiveSite
        ? `/sites/${firstInactiveSite.id}`
        : firstSite
          ? `/sites/${firstSite.id}`
          : "/sites/new",
      ctaLabel: hasActiveSite ? "View site" : "Activate site",
      complete: hasActiveSite,
      disabled: !hasSites,
    },
    {
      id: "print-qr",
      title: "Print site QR",
      description:
        "Put the QR code at the gate so crews can check in on arrival.",
      href: qrHref,
      ctaLabel: "Open QR",
      complete: hasSignIns,
      disabled: !hasSites,
    },
    {
      id: "first-sign-in",
      title: "Capture first sign-in",
      description:
        "Run a test check-in or let the first worker sign in from the QR code.",
      href: hasSignIns ? "/signins" : qrHref,
      ctaLabel: hasSignIns ? "View sign-ins" : "Open QR",
      complete: hasSignIns,
      disabled: !hasActiveSite,
    },
  ];

  const completedCount = tasks.filter((task) => task.complete).length;
  const nextTask =
    tasks.find((task) => !task.complete && !task.disabled) ??
    tasks.find((task) => !task.complete);

  return {
    shouldShow: !hasActiveSite || !hasSignIns,
    progressLabel: `${completedCount} of ${tasks.length} complete`,
    primaryHref: nextTask?.href ?? "/dashboard",
    primaryLabel: nextTask?.ctaLabel ?? "View dashboard",
    tasks,
  };
}
